/**
 * @module ui/tabs
 * @description The prototype object for the "tabs" component
 */
import ee from "lib/eventemitter";
import { getById, qsa } from "utils/utils";

const TabsPrototype = {
  init(list, updateHash = false) {
    if (!list) {
      return;
    }
    // Identify DOM elements
    this.tabList = list;
    this.tabItems = qsa(".c-tabs__item", this.tabList);
    this.tabLinks = qsa(".c-tabs__link", this.tabList);
    this.tabPanel = getById(this.tabList.getAttribute("aria-controls"));
    this.tabPanels = qsa(".c-tabs__panel", this.tabPanel);
    this.updateHash = updateHash;
    this.enabled = null;

    this.handleTabClickBound = this.handleTabClick.bind(this);
    this.handleTabKeyDownBound = this.handleTabKeyDown.bind(this);
  },

  enable() {
    if (this.enabled === true) {
      return;
    }
    this.currentIndex = 0;
    this.currentId = null;

    this.tabItems.forEach((item, index) => {
      if (index === 0) {
        item.classList.add("c-tabs__item--active");
      }
    });
    this.tabLinks.forEach((item, index) => {
      if (index > 0) {
        item.setAttribute("tabindex", "-1");
      } else {
        item.setAttribute("aria-selected", "true");
      }
    });
    this.tabPanels.forEach((item, index) => {
      if (index > 0) {
        item.setAttribute("hidden", "hidden");
      }
      // Restore attributes that may have been removed by the disable() method
      if (!item.getAttribute("role") && item.dataset.role) {
        item.setAttribute("role", item.dataset.role);
      }
      if (!item.getAttribute("aria-labelledby") && item.dataset.ariaLabelledby) {
        item.setAttribute("aria-labelledby", item.dataset.ariaLabelledby);
      }
      item.setAttribute("tabindex", "-1");
    });
    this.tabList.removeAttribute("hidden");
    this.tabList.addEventListener("click", this.handleTabClickBound);
    this.tabList.addEventListener("keydown", this.handleTabKeyDownBound);
    this.enabled = true;
  },

  disable() {
    if (this.enabled === false) {
      return;
    }
    this.currentIndex = 0;
    this.currentId = null;

    this.tabItems.forEach(item => {
      item.classList.remove("c-tabs__item--active");
    });
    this.tabLinks.forEach(item => {
      item.removeAttribute("tabindex");
      item.removeAttribute("aria-selected");
    });
    this.tabPanels.forEach(item => {
      // Reveal all of the panels
      item.removeAttribute("hidden");
      // Remove attributes that are no longer valid when the tabs are hidden,
      // all of the panels are revealed, and user interaction is disabled
      item.dataset.role = item.getAttribute("role");
      item.dataset.ariaLabelledby = item.getAttribute("aria-labelledby");
      item.removeAttribute("role");
      item.removeAttribute("aria-labelledby");
      item.removeAttribute("tabindex");
    });
    // Hide all of the tabs
    this.tabList.setAttribute("hidden", "hidden");
    this.tabList.removeEventListener("click", this.handleTabClickBound);
    this.tabList.removeEventListener("keydown", this.handleTabKeyDownBound);
    this.enabled = false;
  },

  handleTabClick(event) {
    if (event.target.classList.contains("c-tabs__link")) {
      event.preventDefault();
      this.handleTabInteraction(this.tabLinks.indexOf(event.target));
    }
  },

  handleTabKeyDown(event) {
    const index = this.tabLinks.indexOf(event.target);

    // Left and right arrows
    if (event.which === 37 || event.which === 39) {
      event.preventDefault();
      this.handleTabInteraction(index, event.which);
    }
    // Down arrow
    if (event.which === 40) {
      event.preventDefault();
      this.handleTabPanelFocus(index);
    }
  },

  handleTabInteraction(index, arrowDirection = null, emitEvent = true, focusTab = true, updateHashOverride = null) {
    const updateHash = updateHashOverride !== null ? updateHashOverride : this.updateHash;
    const currentIndex = this.currentIndex;
    let newIndex = index;

    if (arrowDirection) {
      if (arrowDirection === 37) { // Left arrow
        newIndex = index - 1;
      } else {
        newIndex = index + 1;
      }
    }
    // Wrap around when user arrows beyond the first or last tab
    if (newIndex < 0) {
      newIndex = this.tabLinks.length - 1;
    } else if (newIndex === this.tabLinks.length) {
      newIndex = 0;
    }
    // Deactivate current tab
    this.tabLinks[currentIndex].setAttribute("tabindex", "-1");
    this.tabLinks[currentIndex].removeAttribute("aria-selected");
    this.tabItems[currentIndex].classList.remove("c-tabs__item--active");

    // Activate new tab
    this.tabLinks[newIndex].setAttribute("aria-selected", "true");
    this.tabItems[newIndex].classList.add("c-tabs__item--active");
    this.tabLinks[newIndex].removeAttribute("tabindex");

    // Update tab panels
    this.tabPanels[currentIndex].setAttribute("hidden", "hidden");
    this.tabPanels[newIndex].removeAttribute("hidden");

    this.currentId = this.tabLinks[newIndex].getAttribute("id"); // string or null
    this.currentIndex = newIndex; // number

    if (updateHash) {
      window.location.hash = this.tabLinks[newIndex].hash;
      // Changing the hash will automatically focus the target element
      // (i.e. the panel) in some browsers, but potentially not every browser
      this.tabPanels[newIndex].focus();
    }
    if (focusTab) {
      this.tabLinks[newIndex].focus();
    }
    if (emitEvent) {
      ee.trigger("tabs:tab-activated", [this, this.currentIndex, this.currentId]);
    }
  },

  handleTabPanelFocus(index) {
    this.tabPanels[index].focus();
  }
};

export default TabsPrototype;
